<template>
    <!-- Loop through each document in results and create a key for each item -->
    <div v-for="document in results" :key="document.id" 
        class="flex items-start space-x-2 border-b-[1px] border-b-gray-200"
        @mouseenter="onDocumentHover(document)"
        @mouseleave="onDocumentLeave"
    >

        <div class="flex rounded-md shadow-lg bg-white hover:bg-gray-100">

            <!-- Container with border and padding for each document -->
            <div class="px-3 2xl:px-5 py-3 2xl:py-4">
                <!-- Spacing between elements within the container -->
                <div>
                    <!-- Title section with a link to the dataset page -->
                    <h4>
                        <router-link
                            class="text-tethys-blue1-dark line-clamp-3 lg:line-clamp-1 pb-1 font-bold no-underline cursor-pointer text-xs 2xl:text-sm"
                            :to="{ name: 'dataset', params: { datasetId: document.id } }"
                            @click="onDocumentClick(document)"
                        >
                            <!-- {{ document.title_output }} -->
                            <span v-if="document.language.toUpperCase() == $i18n.locale">
                                <p>{{ document.title }}</p>
                            </span>
                            <span v-else>
                                <p v-if="document.title_additional[0]">{{ document.title_additional[0] }}</p>
                                <p v-else>{{ document.title }}</p>
                            </span>
                        </router-link>

                    </h4>
                    <!-- DOI section; displayed only if an identifier exists -->
                    <span v-if="document.identifier && document.identifier.length > 0" class="">
                        <!-- DOI label -->
                        <span class="inline-block px-1 py-0.5 2xl:py-1 mt-1 text-xs font-normal 2xl:font-bold text-gray-500 bg-gray-200 rounded shadow-sm" :title="$t('data_type')">
                            DOI
                        </span>
                        <a
                            target="_blank"
                            :href="'https://doi.org/' + document.identifier[0]"
                            class="text-tethys-blue2-dark font-thin 2xl:font-normal text-xs pl-2"
                        >
                            {{ "https://doi.org/" + document.identifier[0] }}
                            <span class="px-2 text-xs">&#10148;</span>
                        </a>
                        
                        <!-- Display AUTHOR information conditionally -->
                        <span v-if="document.author && document.author.length > 0" class="text-gray-500 pointer-events-none text-xs">
                            <!-- Single author -->
                            <span v-if="document.author.length === 1" >
                                {{ simplifyAuthor(document.author[0]) }}
                            </span>
                            <!-- Multiple authors (2-3), separated by semicolons -->
                            <span v-if="document.author.length > 1 && document.author.length < 3">
                                <span
                                    v-for="(author, index) in document.author"
                                    :key="index"
                                >
                                    {{ simplifyAuthor(author) }}
                                    <span v-if="index < document.author.length - 1">; </span>
                                </span>
                            </span>
                            <!-- More than three authors; first two followed by "et al." -->
                            <span v-if="document.author.length >= 3" >
                                <span
                                    v-for="(author, index) in document.author.slice(0, 2)"
                                    :key="index"
                                >
                                    {{ simplifyAuthor(author) }}
                                    <span v-if="index < 1">; </span>
                                </span>
                                et al.
                            </span>
                        </span>
                    </span>
                    <!-- ABSTRACT section -->
                    <p class="pt-2 line-clamp-3 sm:line-clamp-2 relative overflow-hidden">
                        <!-- Abstract text with clamping and ellipsis -->
                        <span class="font-light text-xs">
                            <span v-if="document.language.toUpperCase() === $i18n.locale">
                                {{ document.abstract[0] }}
                            </span>
                            <span v-else>
                                <span v-if="document.abstract[1]">{{ document.abstract[1] }}</span>
                                <span v-else>{{ document.abstract[0] }}</span>
                            </span>
                        </span>
                    </p>

                    <!-- Publication Date, Document type and open access information -->
                    <p class="pt-2">
                        <!-- Publication date formatted by a conversion function -->
                        <span class="inline-block pl-0 pr-3 py-1 text-[8pt] font-thin 2xl:font-normal text-gray-500">
                            <i class="fa-regular fa-calendar text-xs text-tethys-blue2-dark"></i> {{ $t('published') + ": " + convert(document.server_date_published, $i18n.locale) }}
                        </span>
                        <!-- Container for document type and open access labels -->
                        <span class="block sm:inline-block mt-2 sm:mt-0">
                            <!-- Document type label -->
                            <span class="inline-block px-3 py-0.5 2xl:py-1 text-xs font-thin 2xl:font-normal text-tethys-5 italic bg-tethys-3 rounded-xl shadow-sm" :title="$t('data_type')">
                                <i class="fas fa-file pr-1"></i> {{ $t(document.doctype) }}
                            </span>
                            <!-- Open access label, displayed only for specific licenses -->
                            <span
                                v-if="openAccessLicences.includes(document.licence)"
                                class="inline-block px-2.5 py-0.5 2xl:py-1 text-xs font-thin 2xl:font-normal text-tethys-blue1-dark bg-tethys-blue1-light rounded-xl shadow-sm ml-2 sm:ml-0"
                                :title="$t('license_open')"
                            >
                                <i class="fas fa-lock-open"></i>
                            </span>
                            <!-- Embargo label if embargo has not passed -->
                            <!-- TODO: Implement final general processing!! -->
                            <span v-if="document.id == 236"
                                class="inline-block px-3 py-0.5 2xl:py-1 text-xs font-thin 2xl:font-normal text-tethys-5 bg-tethys-3 rounded-xl shadow-sm ml-2 sm:ml-0"
                                :title="$t('dataset_under_embargo_icon')"
                            >
                                <i class="fa-solid fa-hourglass-half"></i>
                            </span>
                        </span>
                    </p>
                </div>
            </div>

        </div>

    </div>
</template>

<script lang="ts">
import VsResultsMap from "./vs-result-map";
export default VsResultsMap;
</script>

<style lang="scss" scoped>

</style>
